import { NewInspectionModalEnum, AssetTypesEnum } from '@types';
import { ASSET_OPTIONS_FOR_CREATE } from '@constants';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Button from '@components/forms/Button';
import { Select } from '@components/forms';
import { Radio } from '@material-tailwind/react';
import {
  CREATE_INSPECTION_TEMPLATE,
  GET_INSPECTION_TEMPLATE,
  UPDATE_INSPECTION_TEMPLATE,
  post
} from '@services';
import clsx from 'clsx';
import ConfirmInspectionModal from '@components/widgets/ConfirmInspectionModal';
import { DOCUMENT_TYPE } from '@constants';
import { LenderDocumentTypeEnum } from '@types';
import { useForm } from 'react-hook-form';
import { Input } from '@components/forms';
import { ReactComponent as ErrorIcon } from '@assets/icons/info_error_light.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTemplateStore } from '@stores';
import { Option } from '@material-tailwind/react';
import { ReactComponent as Info } from '@assets/icons/info-circle.svg';
import OnboardingLayout from '@components/layouts/OnboardingLayout';
import { LAYOUT_FOOTER_OFFSET } from '@components/layouts/layoutFooterHeight';
import { getSelectedLender, getSelectedOrganisationId } from '@utils';
import { ReactComponent as CloseIcon } from '@assets/icons/x_close_icon.svg';
import BlockingChecksSelector from './components/BlockingChecksSelector';
import {
  filterCheckIdsForAsset,
  formatBlockingCheckFooter,
  getBlockingCheckIdsFromRuleTypes,
  getBlockingChecksForAsset,
  getRuleTypesFromCheckIds
} from './components/blockingChecks';
import DocumentVerificationSelector from './components/DocumentVerificationSelector';
import SaveToast from './components/SaveToast';
import TemplateTabBar from './components/TemplateTabBar';
import TemplateTabNav from './components/TemplateTabNav';
import WorkflowChoice, {
  ApprovalWorkflow
} from './components/WorkflowChoice';
import {
  getTemplateTabForField,
  getTemplateTabIndex,
  isTemplateTab,
  TEMPLATE_TABS,
  TemplateTabEnum
} from './components/templateTabs';
import {
  filterDocumentsForAsset,
  filterLoadedDocumentsForAsset,
  getAvailableDocumentOptions,
  INVALID_DOCUMENTS_BY_ASSET,
  normalizeTemplateDocumentTypes,
  serializeTemplateDocumentSelection
} from './components/documentSelection';

enum LocationSharingEnum {
  MANDATORY = 'MANDATORY',
  WARNING_PROMPT = 'WARNING_PROMPT',
  SILENT = 'SILENT'
}

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const LOCATION_SHARING_OPTIONS = [
  { value: LocationSharingEnum.MANDATORY, label: 'Mandatory' },
  {
    value: LocationSharingEnum.WARNING_PROMPT,
    label: 'Recommended (Warning Prompt)'
  },
  { value: LocationSharingEnum.SILENT, label: 'Optional (Silent)' }
];

type SaveState = 'idle' | 'saving' | 'saved' | 'closing';

type TemplateFormValues = {
  name: string;
  description: string;
  inspectionMessage: string;
  selectedDocuments?: string;
  approverEmails?: string;
};

const SAVED_CONFIRMATION_MS = 1600;
const CLOSING_MS = 900;

const RadioChoice = ({
  id,
  name,
  label,
  checked,
  onSelect,
  containerClassName = 'p-2'
}: {
  id: string;
  name: string;
  label: string;
  checked: boolean;
  onSelect: () => void;
  containerClassName?: string;
}) => (
  <div className="inline-flex cursor-pointer items-center" onClick={onSelect}>
    <Radio
      id={id}
      name={name}
      containerProps={{ className: containerClassName }}
      labelProps={{ className: 'cursor-pointer' }}
      checked={checked}
      onChange={onSelect}
      label={label}
    />
  </div>
);

const SAVE_BUTTON_LABEL: Record<SaveState, string> = {
  idle: 'Save & close',
  saving: 'Saving…',
  saved: '\u2713 Saved',
  closing: 'Closing…'
};

// Fields react-hook-form can focus directly; anything else is scrolled into view.
const FOCUSABLE_FIELDS = ['name', 'description', 'inspectionMessage'];

function validateDocumentsForAssetType(
  documents: LenderDocumentTypeEnum[],
  assetType: AssetTypesEnum
): string {
  const invalidDocs = INVALID_DOCUMENTS_BY_ASSET[assetType] || [];
  const invalidDocLabels: string[] = [];

  documents.forEach((doc) => {
    if (invalidDocs.includes(doc)) {
      const docLabel = DOCUMENT_TYPE.find((d) => d.value === doc)?.label;
      if (docLabel) {
        invalidDocLabels.push(docLabel);
      }
    }
  });

  return invalidDocLabels.length > 0
    ? `Cannot include ${invalidDocLabels.join(
        ', '
      )} for the selected asset type`
    : '';
}

const CreateTemplate = () => {
  const { templateObject } = useTemplateStore();
  const [step, setStep] = useState(0);
  const [backToDashboard, setBackToDashboard] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState(NewInspectionModalEnum.close);
  const [successSubmit, setSuccessSubmit] = useState(false);
  const [locationSharing, setLocationSharing] = useState<LocationSharingEnum>(
    LocationSharingEnum.SILENT
  );

  const [isBuyer, setIsBuyer] = useState(true);
  const [assetType, setAssetType] = useState<AssetTypesEnum>(
    AssetTypesEnum.CAR
  );
  const [selectedDocuments, setSelected] = useState<LenderDocumentTypeEnum[]>(
    []
  );
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [toast, setToast] = useState<{
    message: string;
    variant: 'success' | 'error';
  } | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);
  const notificationEmailsRef = useRef<HTMLDivElement>(null);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const saveLockRef = useRef(false);
  const [template, setTemplate] = useState<any>(templateObject);

  const tabParam = searchParams.get('tab');
  const activeTab = isTemplateTab(tabParam)
    ? tabParam
    : TemplateTabEnum.DETAILS;
  const activeTabIndex = getTemplateTabIndex(activeTab);
  const [scrollRequest, setScrollRequest] = useState<{
    field: string;
    tab: TemplateTabEnum;
    id: number;
  } | null>(null);

  const goToTab = (tab: TemplateTabEnum) => {
    if (tab === tabParam) return;
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    setSearchParams(params);
  };

  useEffect(() => {
    if (isTemplateTab(tabParam)) return;
    const params = new URLSearchParams(searchParams);
    params.set('tab', TemplateTabEnum.DETAILS);
    setSearchParams(params, { replace: true });
  }, [tabParam, searchParams, setSearchParams]);

  const [notificationEmails, setNotificationEmails] = useState<string[]>([]);
  const [notificationEmailErrors, setNotificationEmailErrors] = useState<
    string[]
  >([]);
  const [inspectionMessage, setInspectionMessage] = useState<string>(
    'Hi {SellerFirstName} {SellerLastName}, {BuyerFirstName} {BuyerLastName} is interested in purchasing your vehicle. {OrganisationName} needs to verify some details about you and the vehicle before proceeding.\n\n' +
      'Please complete our online inspection process before {ExpiryDate}, after which it will expire.  This process will take {ProcessDurationMinutes} minutes and you will require:\n' +
      '{DocumentList}\n\n' +
      'Get started - {InspectionLink}'
  );
  const [templateHtml, setTemplateHtml] = useState<any>('');
  const [errorMegLength, setErrorMsgLength] = useState<any>(0);
  const liList = [
    '{SellerFirstName}',
    '{SellerLastName}',
    '{BuyerFirstName}',
    '{BuyerLastName}',
    '{OrganisationName}',
    '{ExpiryDate}',
    '{ProcessDurationMinutes}',
    '{DocumentList}',
    '{InspectionLink}'
  ];
  const templateMsg = {
    SellerFirstName: 'Sellerfirst',
    SellerLastName: 'Sellerlast',
    BuyerFirstName: 'Buyerfirst',
    BuyerLastName: 'Buyerlast',
    OrganisationName: 'Acme Corp',
    ExpiryDate: 'dd/mm/yyyy',
    ProcessDurationMinutes: 'X',
    DocumentList: 'Document 1, Document 2, Document 3',
    InspectionLink:
      'https://authentik8.com.au/inspection/login#token=1234567890123456789012345'
  };
  const onDashboardClick = () => {
    setBackToDashboard(true);
    setIsOpenModal(true);
    setModalType(NewInspectionModalEnum.close);
  };
  const onCloseClick = () => {
    setIsOpenModal(true);
    setModalType(NewInspectionModalEnum.close);
  };

  const {
    register,
    setError,
    setFocus,
    handleSubmit,
    clearErrors,
    setValue,
    getValues,
    formState: { errors }
  } = useForm<TemplateFormValues>({
    shouldUnregister: false,
    defaultValues: {
      name: '',
      description: '',
      inspectionMessage
    }
  });

  // Brings the control that failed validation back on screen, switching tab first
  // because only the active panel is mounted.
  const revealField = (field: string) => {
    const tab = getTemplateTabForField(field);
    if (tab !== activeTab) {
      goToTab(tab);
    }
    setScrollRequest({ field, tab, id: Date.now() });
  };

  const failValidation = (field: keyof TemplateFormValues, message: string) => {
    setError(field, { type: 'custom', message });
    revealField(field);
  };

  useEffect(() => {
    if (!scrollRequest || scrollRequest.tab !== activeTab) return;

    const { field } = scrollRequest;
    setScrollRequest(null);

    if (FOCUSABLE_FIELDS.includes(field)) {
      setFocus(field as 'name' | 'description' | 'inspectionMessage');
      return;
    }

    const target =
      field === 'selectedDocuments'
        ? documentRef.current
        : field === 'notificationEmails'
          ? notificationEmailsRef.current
          : containerRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [scrollRequest, activeTab]);

  useEffect(
    () => () => {
      timeoutsRef.current.forEach(clearTimeout);
    },
    []
  );

  const scheduleTimeout = (callback: () => void, delay: number) => {
    timeoutsRef.current.push(setTimeout(callback, delay));
  };

  const handleSaveSuccess = () => {
    setSaveState('saved');
    setToast({ message: 'Inspection template saved', variant: 'success' });
    scheduleTimeout(() => {
      setSaveState('closing');
      setToast({
        message: 'Saved \u2014 closing template…',
        variant: 'success'
      });
      scheduleTimeout(() => navigate('/inspection-templates'), CLOSING_MS);
    }, SAVED_CONFIRMATION_MS);
  };

  const handleSaveFailure = (e: any) => {
    const message =
      e?.response?.data?.error?.message ||
      'Something went wrong while saving. Please try again.';
    saveLockRef.current = false;
    setSaveState('idle');
    setToast({ message, variant: 'error' });
    scheduleTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    getSelectedOrganisationId();
    const selectedLender = getSelectedLender();
    const organisationName = selectedLender?.organisationName || '';
    const newDocStr = (() => {
      const list = new Set<string>();

      // Check for driver's license
      if (selectedDocuments.includes(LenderDocumentTypeEnum.SELLER_ID)) {
        list.add("driver's license");
      }

      // Check for vehicle access related documents
      const vehicleAccessDocs = [
        LenderDocumentTypeEnum.VIN,
        LenderDocumentTypeEnum.REGO_PLATE,
        LenderDocumentTypeEnum.SALE_DETAILS,
        LenderDocumentTypeEnum.MARINE_HIN,
        LenderDocumentTypeEnum.MARINE_REGO_NUMBER,
        LenderDocumentTypeEnum.ODOMETER,
        LenderDocumentTypeEnum.OPERATING_HOURS,
        LenderDocumentTypeEnum.ASSET_FRONT,
        LenderDocumentTypeEnum.ASSET_BACK,
        LenderDocumentTypeEnum.ASSET_LEFT,
        LenderDocumentTypeEnum.ASSET_RIGHT,
        LenderDocumentTypeEnum.MARINE_FRONT,
        LenderDocumentTypeEnum.MARINE_BACK,
        LenderDocumentTypeEnum.MARINE_LEFT,
        LenderDocumentTypeEnum.MARINE_RIGHT,
        LenderDocumentTypeEnum.INTERNAL_FRONT,
        LenderDocumentTypeEnum.INTERNAL_BACK,
        LenderDocumentTypeEnum.INTERNAL_BOOT,
        LenderDocumentTypeEnum.INTERNAL_LIVING_AREA,
        LenderDocumentTypeEnum.INTERNAL_KITCHEN,
        LenderDocumentTypeEnum.INTERNAL_SLEEPING_AREA
      ];
      if (vehicleAccessDocs.some((doc) => selectedDocuments.includes(doc))) {
        list.add('access to the vehicle');
      }

      // Check for registration certificate
      if (selectedDocuments.includes(LenderDocumentTypeEnum.REGO_PAPERS)) {
        list.add('registration certificate');
      }

      // Check for bank statement
      if (selectedDocuments.includes(LenderDocumentTypeEnum.BANK_STATEMENTS)) {
        list.add('recent bank statement');
      }

      // // Check for roadworthy/safety certificate
      // if (
      //   selectedDocuments.includes(LenderDocumentTypeEnum.ROADWORTHY_CERT) ||
      //   selectedDocuments.includes(LenderDocumentTypeEnum.SAFETY_CERT)
      // ) {
      //   list.add('roadworthy / safety certificate');
      // }
      const items = Array.from(list);
      // If there's only one item, return it directly
      if (items.length <= 1) return items.join('');

      // For multiple items, join with commas and add "and" before the last item
      return items.slice(0, -1).join(', \n') + ' and \n' + items.slice(-1);
    })();
    const fullTemplateMsg = {
      ...templateMsg,
      OrganisationName: organisationName,
      DocumentList: newDocStr
    };

    let newTemplateHtml = inspectionMessage;
    Object.entries(fullTemplateMsg).forEach(([key, value]) => {
      if (key === 'InspectionLink') {
        newTemplateHtml = newTemplateHtml.replace(
          new RegExp(`{${key}}`, 'g'),
          `<span style="word-break: break-all;">${value}</span>`
        );
      } else {
        newTemplateHtml = newTemplateHtml.replace(
          new RegExp(`{${key}}`, 'g'),
          value as string
        );
      }
    });

    setTemplateHtml(newTemplateHtml);
    const charCount = newTemplateHtml.replace(/<[^>]+>/g, '').length;

    if (charCount > 600) {
      setErrorMsgLength(charCount);
    } else {
      setErrorMsgLength(0);
    }
  }, [selectedDocuments, inspectionMessage]);
  function onCloseModalClick() {
    setIsOpenModal(false);
    setBackToDashboard(false);
  }
  function handleClose() {
    setIsOpenModal(false);
    setBackToDashboard(false);
    navigate('/inspection-templates');
  }
  const setLenderDocuments = (data: any) => {
    if (selectedDocuments.length === 0) {
      failValidation(
        'selectedDocuments',
        'Please select at least one document.'
      );
      return;
    }

    const assetTypeValidationError = validateDocumentsForAssetType(
      selectedDocuments,
      assetType
    );
    if (assetTypeValidationError) {
      failValidation('selectedDocuments', assetTypeValidationError);
      return;
    }

    // Validate notification emails
    if (notificationEmails.length > 0) {
      const invalidEmails = notificationEmails.filter(
        (email) => !emailRegex.test(email)
      );
      if (invalidEmails.length > 0) {
        setNotificationEmailErrors((errors) =>
          errors.map((err, i) =>
            i < notificationEmails.length &&
            !emailRegex.test(notificationEmails[i])
              ? 'Enter a valid email address.'
              : err
                ? 'Enter a valid email address.'
                : err
          )
        );
        revealField('notificationEmails');
        return;
      }
    }

    // Check NEVDIS dependency
    if (
      selectedDocuments.includes(LenderDocumentTypeEnum.NEVDIS) &&
      !selectedDocuments.includes(LenderDocumentTypeEnum.VIN) &&
      !selectedDocuments.includes(LenderDocumentTypeEnum.REGO_PLATE) &&
      !selectedDocuments.includes(LenderDocumentTypeEnum.REGO_PAPERS)
    ) {
      failValidation(
        'selectedDocuments',
        'VIN, Number Plate or Registration Certificate must be included when NEVDIS is included.'
      );
      return;
    }

    // Check VIN dependency
    if (
      selectedDocuments.includes(LenderDocumentTypeEnum.VIN) &&
      !selectedDocuments.includes(LenderDocumentTypeEnum.NEVDIS)
    ) {
      failValidation(
        'selectedDocuments',
        'NEVDIS external verification must be included when VIN is included.'
      );
      return;
    }

    // Check Registration Certificate dependency
    if (
      selectedDocuments.includes(LenderDocumentTypeEnum.REGO_PAPERS) &&
      !selectedDocuments.includes(LenderDocumentTypeEnum.NEVDIS)
    ) {
      failValidation(
        'selectedDocuments',
        'NEVDIS external verification must be included when Registration Certificate is included.'
      );
      return;
    }

    // Check Number Plate dependency
    if (
      selectedDocuments.includes(LenderDocumentTypeEnum.REGO_PLATE) &&
      !selectedDocuments.includes(LenderDocumentTypeEnum.NEVDIS)
    ) {
      failValidation(
        'selectedDocuments',
        'NEVDIS external verification must be included when Number Plate is included.'
      );
      return;
    }

    // Check MARINE HIN completion requirement
    if (selectedDocuments.includes(LenderDocumentTypeEnum.MARINE_HIN)) {
      // HIN requires user input - no additional dependencies but validation on client side
    }

    // Check MARINE_PPSR dependency
    if (
      selectedDocuments.includes(LenderDocumentTypeEnum.MARINE_PPSR) &&
      !selectedDocuments.includes(LenderDocumentTypeEnum.MARINE_HIN)
    ) {
      failValidation(
        'selectedDocuments',
        'HIN must be included when Marine PPSR is included.'
      );
      return;
    }

    // Check HIN dependency for MARINE_PPSR
    if (
      selectedDocuments.includes(LenderDocumentTypeEnum.MARINE_HIN) &&
      !selectedDocuments.includes(LenderDocumentTypeEnum.MARINE_PPSR) &&
      (assetType === AssetTypesEnum.MARINE ||
        assetType === AssetTypesEnum.MARINE_WITH_TRAILER)
    ) {
      failValidation(
        'selectedDocuments',
        'Marine PPSR external verification must be included when HIN is included.'
      );
      return;
    }

    if (errorMegLength > 640) {
      revealField('inspectionMessage');
      return;
    }

    // Validate approver emails when Advanced approval is selected
    if (criteriaEnabled && advancedApprovalEnabled) {
      const validApproverEmails = approverEmails.filter(
        (email) => email.trim() !== ''
      );
      if (validApproverEmails.length === 0) {
        failValidation(
          'approverEmails',
          'Enter at least one approver email address.'
        );
        return;
      }

      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const invalidEmails = validApproverEmails.filter(
        (email) => !emailRegex.test(email)
      );
      if (invalidEmails.length > 0) {
        failValidation('approverEmails', 'Enter a valid email address.');
        return;
      }
    }
    // Check buyer details usage when not required
    if (
      !isBuyer &&
      (inspectionMessage.includes('{BuyerFirstName}') ||
        inspectionMessage.includes('{BuyerLastName}'))
    ) {
      revealField('inspectionMessage');
      return;
    }

    // Check for required inspection link
    if (!inspectionMessage.includes('{InspectionLink}')) {
      revealField('inspectionMessage');
      return;
    }
    const documentSelection =
      serializeTemplateDocumentSelection(selectedDocuments);
    const params: any = {
      assetType,
      name: data.name,
      description: data.description,
      buyerRequired: isBuyer,
      documentTypes: documentSelection.documentTypes,
      notificationEmails: notificationEmails,
      inspectionConfig: {
        roadworthyIfUnregistered: documentSelection.roadworthyIfUnregistered,
        roadworthyByState: documentSelection.roadworthyByState,
        locationSharing,
        isExceptionApprovalRequired:
          criteriaEnabled && advancedApprovalEnabled
      }
    };

    // Same as production/development: only send approvalEmails for Advanced.
    // UpdateInspectionTemplate treats a present (even empty) list as a write.
    if (criteriaEnabled && advancedApprovalEnabled) {
      params.approvalEmails = approverEmails.filter(
        (email) => email.trim() !== ''
      );
    }

    // Only add sharedLenderIds if it's not empty
    if (sharedLenderIds && sharedLenderIds.length > 0) {
      params.inspectionConfig.sharedLenderIds = sharedLenderIds;
    }

    if (inspectionMessage !== '') {
      params.sellerInviteMessage = inspectionMessage;
    }

    params.ruleTypes = criteriaEnabled
      ? getRuleTypesFromCheckIds(
          filterCheckIdsForAsset(selectedBlockingCheckIds, assetType)
        )
      : [];

    if (template && template.templateId) {
      if (saveLockRef.current) return;
      saveLockRef.current = true;
      setSaveState('saving');
      post(
        UPDATE_INSPECTION_TEMPLATE,
        {
          ...params,
          templateId: template.templateId
        },
        false
      )
        .then(() => {
          handleSaveSuccess();
        })
        .catch(handleSaveFailure);
    } else {
      const userId = localStorage.getItem('userId');

      const lenders = localStorage.getItem(`lenders_${userId}`);
      if (lenders != null) {
        if (saveLockRef.current) return;
        saveLockRef.current = true;
        setSaveState('saving');
        post(
          CREATE_INSPECTION_TEMPLATE,
          {
            lenderOrganisationId: getSelectedOrganisationId(),
            ...params
          },
          false
        )
          .then((data) => {
            setTemplate(data);
            handleSaveSuccess();
          })
          .catch(handleSaveFailure);
      }
    }
  };
  const updateSelectedDocuments = (documents: LenderDocumentTypeEnum[]) => {
    const uniqueDocuments = Array.from(new Set(documents));
    const validationError = validateDocumentsForAssetType(
      uniqueDocuments,
      assetType
    );

    if (validationError) {
      setError('selectedDocuments', {
        type: 'custom',
        message: validationError
      });
    } else {
      clearErrors('selectedDocuments');
    }
    setSelected(uniqueDocuments);
  };

  useEffect(() => {
    if (templateObject && templateObject.templateId) {
      post(GET_INSPECTION_TEMPLATE, {
        templateId: templateObject.templateId
      }).then((res) => {
        if (res.template) {
          const targetTemplate = res.template;
          setTemplate(targetTemplate);
          setNotificationEmails(targetTemplate.notificationEmails);

          const loadedCheckIds = getBlockingCheckIdsFromRuleTypes(
            targetTemplate.ruleTypes
          );
          const advancedApproval = Boolean(
            targetTemplate.inspectionConfig?.isExceptionApprovalRequired
          );
          setSelectedBlockingCheckIds(loadedCheckIds);
          setCriteriaEnabled(loadedCheckIds.length > 0 || advancedApproval);
          setAdvancedApprovalEnabled(advancedApproval);
          if (targetTemplate.approvalEmails?.length) {
            setApproverEmails(targetTemplate.approvalEmails);
            setApproverEmailErrors(
              targetTemplate.approvalEmails.map(() => '')
            );
          }
          const targetAssetType = targetTemplate.assetType as AssetTypesEnum;
          const normalizedDocuments = normalizeTemplateDocumentTypes(
            targetTemplate.documentTypes,
            targetTemplate.inspectionConfig
          );
          const validDocuments = filterLoadedDocumentsForAsset(
            normalizedDocuments,
            targetAssetType
          );
          if (targetTemplate.inspectionConfig.locationSharing) {
            setLocationSharing(targetTemplate.inspectionConfig.locationSharing);
          }
          // Store sharedLenderIds from inspectionConfig
          if (targetTemplate.inspectionConfig.sharedLenderIds) {
            setSharedLenderIds(targetTemplate.inspectionConfig.sharedLenderIds);
          }
          setSelected(validDocuments);

          setValue('name', targetTemplate.name);
          setValue('description', targetTemplate.description);
          setIsBuyer(targetTemplate.buyerRequired);
          setAssetType(targetAssetType);

          if (targetTemplate.sellerInviteMessage) {
            setInspectionMessage(targetTemplate.sellerInviteMessage);
            setValue('inspectionMessage', targetTemplate.sellerInviteMessage);
          }
        }
      });
      return;
    }
    // The template store defaults to `{}` (and persist may rehydrate that),
    // so treat any object without a templateId as a new template.
    setSelected(
      getAvailableDocumentOptions(AssetTypesEnum.CAR).map(
        (option) => option.value as LenderDocumentTypeEnum
      )
    );
  }, [templateObject]);
  const handleAssetTypeChange = (newType: AssetTypesEnum) => {
    setAssetType(newType);
    setSelected((documents) => filterDocumentsForAsset(documents, newType));
    clearErrors('selectedDocuments');

    setSelectedBlockingCheckIds((checkIds) =>
      filterCheckIdsForAsset(checkIds, newType)
    );
  };

  const [criteriaEnabled, setCriteriaEnabled] = useState(false);
  const [selectedBlockingCheckIds, setSelectedBlockingCheckIds] = useState<
    string[]
  >([]);
  const [sharedLenderIds, setSharedLenderIds] = useState<string[]>([]);

  const [advancedApprovalEnabled, setAdvancedApprovalEnabled] =
    useState<boolean>(false);
  const [approverEmails, setApproverEmails] = useState<string[]>(['']);
  const [approverEmailErrors, setApproverEmailErrors] = useState<string[]>([
    ''
  ]);
  const selectedWorkflow: ApprovalWorkflow = advancedApprovalEnabled
    ? 'advanced'
    : 'basic';

  const availableDocumentOptions = useMemo(
    () => getAvailableDocumentOptions(assetType),
    [assetType]
  );
  const selectedDocumentCount = useMemo(() => {
    const selected = new Set(selectedDocuments);
    return availableDocumentOptions.filter((option) =>
      selected.has(option.value as LenderDocumentTypeEnum)
    ).length;
  }, [availableDocumentOptions, selectedDocuments]);

  const availableBlockingChecks = useMemo(
    () => getBlockingChecksForAsset(assetType),
    [assetType]
  );
  const blockingCheckCount = criteriaEnabled
    ? availableBlockingChecks.filter((check) =>
        selectedBlockingCheckIds.includes(check.id)
      ).length
    : 0;

  const tabChips = {
    [TemplateTabEnum.DOCUMENTS]: {
      label: `${selectedDocumentCount} of ${availableDocumentOptions.length}`
    },
    [TemplateTabEnum.RULES]: criteriaEnabled
      ? {
          label: `${blockingCheckCount} of ${availableBlockingChecks.length}`
        }
      : { label: '(Optional)', variant: 'optional' as const }
  };

  const handleSaveAndClose = () => {
    if (saveState !== 'idle' || saveLockRef.current) return;
    handleSubmit(
      (data) => {
        // Only the active tab is mounted, so RHF can skip unmounted required
        // fields. Re-check the details fields that every save still needs.
        const name = String(data.name ?? getValues('name') ?? '');
        const description = String(
          data.description ?? getValues('description') ?? ''
        );
        if (!name.trim()) {
          failValidation('name', 'Name is required');
          return;
        }
        if (!description.trim()) {
          failValidation('description', 'Description is required');
          return;
        }
        if (description.length > 280) {
          failValidation(
            'description',
            'Inspection Template description cannot be longer than 280 characters.'
          );
          return;
        }
        // Send the raw values so the API payload matches the pre-tab form.
        setLenderDocuments({ ...data, name, description });
      },
      (formErrors) => {
        const [firstField] = Object.keys(formErrors);
        revealField(firstField || 'name');
      }
    )();
  };

  return (
    <OnboardingLayout
      step={step}
      onChangeStep={setStep}
      successSubmit={successSubmit}
      backToDashboard={() => onDashboardClick()}
      title="Create inspection template"
      type="template"
      onClose={() => onCloseClick()}
      tabs={
        <TemplateTabBar
          activeTab={activeTab}
          chips={tabChips}
          onChange={goToTab}
        />
      }>
      <div className="w-full max-w-[768px] pb-40 md:pb-24" ref={containerRef}>
        <div
          id={`template-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`template-tab-${activeTab}`}>
          {activeTab === TemplateTabEnum.DETAILS && (
            <div className="flex flex-col">
              <div className="text-[20px] font-semibold text-grey-900">
                Inspection Details
              </div>
              <p className="mt-1 mb-4 text-sm text-grey-500">
                Names and defaults applied every time this template is used.
              </p>
              <div className="min-w-0 rounded-lg border border-grey-200 bg-white p-4 md:p-6">
                <div className="mb-4">
                  <p className="text-grey-700 text-sm mb-1.5 font-semibold">
                    Inspection template name
                  </p>
                  <Input
                    type="text"
                    {...register(`name`, { required: 'Name is required' })}
                    placeholder="Inspection template name"
                    onBlur={() => {
                      clearErrors();
                    }}
                    icon={errors.name && <ErrorIcon />}
                  />
                  {errors.name ? (
                    <span className="text-error-light text-2sm mt-[6px]">
                      {errors.name?.message?.toString()}
                    </span>
                  ) : (
                    <span className="field-hint mt-[6px]">
                      Displays as an option when creating a new inspection
                    </span>
                  )}
                </div>
                <div className="mb-4">
                  <p className="text-grey-700 text-sm mb-1.5 font-semibold">
                    Description
                  </p>
                  <textarea
                    className="w-full h-32 border rounded-lg py-3 px-3.5 gap-2 border-grey-300"
                    {...register(`description`, {
                      required: 'Description is required',
                      maxLength: {
                        value: 280,
                        message:
                          'Inspection Template description cannot be longer than 280 characters.'
                      }
                    })}
                    placeholder="Inspection template description"
                    onBlur={() => {
                      clearErrors();
                    }}
                  />
                  {errors.description && (
                    <span className="text-error-light text-2sm mt-[6px]">
                      {errors.description?.message?.toString()}
                    </span>
                  )}
                </div>
                <div className="mb-4 flex flex-col gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-grey-700">
                      Asset Type
                    </label>
                    <Select
                      value={assetType}
                      onChange={(e) =>
                        handleAssetTypeChange(e as AssetTypesEnum)
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                      {ASSET_OPTIONS_FOR_CREATE.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                    <p className="field-hint mt-2">
                      Choose the asset type relevant to this inspection. The
                      selected asset type customises the seller&apos;s
                      inspection experience with appropriate wording and images.
                    </p>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-grey-700">
                      Location Sharing
                    </label>
                    <Select
                      value={locationSharing}
                      onChange={(value) =>
                        setLocationSharing(value as LocationSharingEnum)
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                      {LOCATION_SHARING_OPTIONS.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                    <p className="field-hint mt-2">
                      Controls whether location sharing is enforced, encouraged,
                      or optional during the seller&apos;s inspection process.
                    </p>
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-grey-700 text-sm mb-1.5 font-semibold">
                    Are &apos;Buyer&apos; details required?
                  </p>

                  <RadioChoice
                    id="buyer-required-yes"
                    name="type"
                    label="Yes"
                    checked={isBuyer}
                    onSelect={() => setIsBuyer(true)}
                  />
                  <RadioChoice
                    id="buyer-required-no"
                    name="type"
                    label="No"
                    checked={!isBuyer}
                    onSelect={() => setIsBuyer(false)}
                    containerClassName="p-2 ml-4"
                  />
                  <p className="field-hint">
                    Choosing &apos;Yes&apos; to the above will require entry of
                    Buyer First Name and Last Name details when creating an
                    inspection.
                  </p>
                </div>

                <div
                  className="border-t border-grey-200 pt-6 mt-6"
                  ref={notificationEmailsRef}>
                  <p className="mb-1.5 text-sm font-semibold text-grey-700">
                    Additional Notification Emails (Template-specific)
                  </p>
                  <p className="field-hint mb-3">
                    These email addresses will receive inspection report
                    notifications for inspections using this template. They are
                    in addition to the global notification email configured in
                    Settings.
                  </p>
                  <div className="w-full min-w-0 max-w-[520px]">
                    {notificationEmails.map((email, idx) => (
                      <div key={idx} className="mb-2 flex min-w-0 items-center">
                        <Input
                          type="email"
                          placeholder="example@email.com"
                          value={email}
                          className="min-w-0 w-full"
                          inputClassName="w-full"
                          onChange={(e) => {
                            const val = e.target.value;
                            setNotificationEmails((emails) =>
                              emails.map((em, i) => (i === idx ? val : em))
                            );
                            // Validate and clear error immediately if valid
                            // Check for duplicate emails
                            const updatedEmails = notificationEmails.map(
                              (em, i) => (i === idx ? val : em)
                            );
                            const isDuplicate =
                              val &&
                              updatedEmails.some(
                                (email, i) =>
                                  i !== idx &&
                                  email.toLowerCase() === val.toLowerCase()
                              );

                            setNotificationEmailErrors((errors) =>
                              errors.map((err, i) =>
                                i === idx
                                  ? val && emailRegex.test(val) && !isDuplicate
                                    ? ''
                                    : err
                                  : err
                              )
                            );
                          }}
                          onBlur={(e) => {
                            const val = e.target.value;
                            // Check for duplicate emails
                            const isDuplicate =
                              val &&
                              notificationEmails.some(
                                (email, i) =>
                                  i !== idx &&
                                  email.toLowerCase() === val.toLowerCase()
                              );

                            setNotificationEmailErrors((errors) =>
                              errors.map((err, i) =>
                                i === idx
                                  ? val && !emailRegex.test(val)
                                    ? 'Enter a valid email address.'
                                    : isDuplicate
                                      ? 'This email address has already been added.'
                                      : ''
                                  : err
                              )
                            );
                          }}
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="ml-2 text-grey-400 hover:text-grey-700 cursor-pointer"
                          aria-label="Remove email"
                          onClick={() => {
                            setNotificationEmails((emails) =>
                              emails.filter((_, i) => i !== idx)
                            );
                            setNotificationEmailErrors((errors) =>
                              errors.filter((_, i) => i !== idx)
                            );
                          }}>
                          <CloseIcon />
                        </button>
                      </div>
                    ))}
                  </div>

                  {notificationEmailErrors.some(Boolean) && (
                    <div className="text-error-light text-2sm mt-1">
                      {notificationEmailErrors.map(
                        (err, idx) => err && <div key={idx}>{err}</div>
                      )}
                    </div>
                  )}
                  {notificationEmails.length < 5 && (
                    <button
                      type="button"
                      className="mt-2 rounded border border-grey-300 bg-white px-4 py-2 text-[13.5px] font-normal text-[#1257A0] hover:bg-grey-50"
                      onClick={() => {
                        setNotificationEmails((emails) => [...emails, '']);
                        setNotificationEmailErrors((errors) => [...errors, '']);
                      }}>
                      + Add another email address
                    </button>
                  )}
                  {notificationEmails.length >= 5 && (
                    <p className="field-hint mt-2">
                      Maximum 5 notification emails allowed
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === TemplateTabEnum.INVITATION && (
            <div className="flex w-full flex-col">
              <div className="text-[20px] font-semibold text-grey-900">
                Invitation Message
              </div>
              <p className="mt-1 mb-5 text-sm text-grey-500">
                The SMS the seller receives. Preview updates as you type.
              </p>
              <div className="flex flex-col items-start gap-5 md:flex-row md:gap-[30px]">
                <div className="w-full min-w-0 rounded-lg border border-grey-200 bg-white p-4 md:flex-1 md:p-6">
                  <p className="mb-1.5 text-sm font-semibold text-grey-700">
                    Inspection Invitation SMS Message
                  </p>
                  <textarea
                    className="h-[320px] w-full rounded-lg border border-grey-300 px-3.5 py-3 text-[13.5px] leading-5 text-[rgb(20,40,61)] !font-['Inter_Tight',Helvetica,Arial,sans-serif]"
                    {...register(`inspectionMessage`, {
                      required: 'Name is required',
                      value: inspectionMessage
                    })}
                    onChange={(e) => {
                      setValue('inspectionMessage', e.target.value);
                      setInspectionMessage(e.target.value);
                    }}
                    placeholder="Inspection template description"
                    onBlur={() => {
                      clearErrors();
                    }}
                  />
                  {errorMegLength > 640 && (
                    <span className="text-error-light text-2sm mt-[6px]">
                      Message exceeds 640 characters. It may be split into
                      multiple SMS.
                    </span>
                  )}
                  {errorMegLength > 600 && errorMegLength < 640 && (
                    <span className="text-error-light text-2sm mt-[6px]">
                      Message is approaching 640 characters limit.
                    </span>
                  )}
                  {!isBuyer &&
                    (inspectionMessage.includes('{BuyerFirstName}') ||
                      inspectionMessage.includes('{BuyerLastName}')) && (
                      <div className="text-error-light text-2sm mt-[6px]">
                        {'{BuyerFirstName} '} and {'{BuyerLastName} '}
                        personalisation variables cannot be used when Buyer
                        details are not required in the inspection.
                      </div>
                    )}
                  {!inspectionMessage.includes('{InspectionLink}') &&
                    inspectionMessage !== '' && (
                      <div className="text-error-light text-2sm mt-[6px]">
                        Message must include the {'{InspectionLink} '}{' '}
                        variable.
                      </div>
                    )}
                  <p className="mt-5 mb-2 text-[11px] font-semibold uppercase tracking-wide text-grey-700">
                    Personalisation variables
                  </p>
                  <div className="flex flex-wrap content-start gap-1.5">
                    {liList.map((item) => (
                      <span
                        key={item}
                        className="rounded border border-[#B2DDFF] bg-[#EFF8FF] px-1.5 py-0.5 font-mono text-[12px] leading-4 text-secondary">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mx-auto w-full max-w-[294px] shrink-0 rounded-[42px] bg-[#101820] p-[10px] shadow-[0_12px_30px_rgba(16,24,40,0.16)] md:mx-0">
                  <div className="overflow-hidden rounded-[32px] bg-white">
                    <div className="flex items-center justify-between px-5 pt-2.5 text-[10px] font-semibold text-grey-900">
                      <span>9:41</span>
                      <div className="flex items-center gap-[4px] text-grey-900">
                        <svg
                          width="15"
                          height="10"
                          viewBox="0 0 15 10"
                          aria-hidden="true">
                          <rect x="0" y="6" width="2.2" height="4" rx="0.4" fill="currentColor" />
                          <rect x="3.6" y="4" width="2.2" height="6" rx="0.4" fill="currentColor" />
                          <rect x="7.2" y="2" width="2.2" height="8" rx="0.4" fill="currentColor" />
                          <rect
                            x="10.8"
                            y="0"
                            width="2.2"
                            height="10"
                            rx="0.4"
                            fill="currentColor"
                            opacity="0.35"
                          />
                        </svg>
                        <svg
                          width="12"
                          height="10"
                          viewBox="0 0 12 10"
                          aria-hidden="true">
                          <path
                            d="M6 8.2a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Zm-2.5-2.1a3.6 3.6 0 0 1 5 0l-.7.7a2.6 2.6 0 0 0-3.6 0l-.7-.7Zm-1.7-1.7a6 6 0 0 1 8.4 0l-.7.7a5 5 0 0 0-7 0l-.7-.7Z"
                            fill="currentColor"
                          />
                        </svg>
                        <svg
                          width="17"
                          height="10"
                          viewBox="0 0 17 10"
                          aria-hidden="true">
                          <rect
                            x="0.5"
                            y="1"
                            width="14"
                            height="8"
                            rx="1.6"
                            stroke="currentColor"
                            fill="none"
                          />
                          <rect x="2" y="2.4" width="9.5" height="5.2" rx="0.6" fill="currentColor" />
                          <rect x="15" y="3.4" width="1.4" height="3.2" rx="0.4" fill="currentColor" />
                        </svg>
                      </div>
                    </div>
                    <p className="py-2 text-center text-xs font-semibold text-grey-900">
                      +61 483 965 724
                    </p>
                    <div className="border-t border-grey-200" />
                    <div className="min-h-[340px] px-3.5 py-3.5">
                      <div className="rounded-[17px] bg-[#F2F2F7] px-3.5 py-3.5 text-[11px] leading-[16px] text-grey-900">
                        {templateHtml
                          .split('\n')
                          .map((paragraph: string, index: number) => (
                            <React.Fragment key={index}>
                              {paragraph
                                .split(/(<span.*?<\/span>)/)
                                .map((part, partIndex) => {
                                  if (part.startsWith('<span')) {
                                    const match = part.match(
                                      /<span style="([^"]*)">(.*?)<\/span>/
                                    );
                                    if (match) {
                                      const [, , content] = match;
                                      return (
                                        <span
                                          key={partIndex}
                                          className="text-grey-900"
                                          style={{
                                            wordBreak: 'break-word'
                                          }}>
                                          {content}
                                        </span>
                                      );
                                    }
                                  }
                                  return part;
                                })}
                              {index <
                                templateHtml.split('\n').length - 1 && <br />}
                            </React.Fragment>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === TemplateTabEnum.DOCUMENTS && (
            <div className="flex flex-col">
              <div ref={documentRef} className="mb-4">
                <h2 className="text-[20px] font-semibold text-grey-900">
                  Documents & Verifications
                </h2>
                <p className="mt-1 text-sm text-grey-500">
                  Turn on documents you require for your inspections.
                </p>
              </div>
              <div>
                <DocumentVerificationSelector
                  assetType={assetType}
                  options={availableDocumentOptions}
                  selectedDocuments={selectedDocuments}
                  onChange={updateSelectedDocuments}
                />
                {errors.selectedDocuments && (
                  <span className="mt-[6px] block text-2sm text-error-500">
                    {errors.selectedDocuments?.message?.toString()}
                  </span>
                )}
              </div>
            </div>
          )}

          {activeTab === TemplateTabEnum.RULES && (
            <div className="flex flex-col">
              <div className="mb-4 flex items-center justify-between gap-4">
                <h2 className="text-[20px] font-semibold text-grey-900">
                  Lender Criteria Rules{' '}
                  <span className="text-[16px] font-normal text-grey-400">
                    (Optional)
                  </span>
                </h2>
                <div className="flex flex-shrink-0 items-center gap-2 rounded-md px-2 py-1 hover:bg-grey-50">
                  <span
                    className={clsx(
                      'text-sm font-medium',
                      criteriaEnabled ? 'text-grey-400' : 'text-grey-700'
                    )}>
                    Off
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-label="Lender criteria blocking checks"
                    aria-checked={criteriaEnabled}
                    onClick={() => setCriteriaEnabled((enabled) => !enabled)}
                    className={clsx(
                      'relative inline-flex h-6 w-11 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2',
                      criteriaEnabled ? 'bg-secondary' : 'bg-grey-200'
                    )}>
                    <span
                      aria-hidden="true"
                      className={clsx(
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                        criteriaEnabled ? 'translate-x-5' : 'translate-x-0'
                      )}
                    />
                  </button>
                  <span
                    className={clsx(
                      'text-sm font-medium',
                      criteriaEnabled ? 'text-grey-700' : 'text-grey-400'
                    )}>
                    On
                  </span>
                </div>
              </div>
              <p className="mb-6 text-sm text-grey-500">
                Turn on blocking checks to hold inspections that do not meet
                your lending criteria. Checks left off are still run and
                recorded on the inspection; they just do not block.
              </p>
              {!criteriaEnabled ? (
                <div
                  role="status"
                  className="rounded-2xl border border-dashed border-grey-300 bg-white px-8 py-10 text-center">
                  <p className="text-sm leading-6 text-grey-700">
                    No blocking checks. Every completed inspection moves
                    straight to{' '}
                    <span className="font-semibold">Submitted</span> for the
                    lender to review.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-8">
                  <WorkflowChoice
                    value={selectedWorkflow}
                    onChange={(workflow) =>
                      setAdvancedApprovalEnabled(workflow === 'advanced')
                    }>
                    {advancedApprovalEnabled && (
                    <div>
                      <label className="flex items-center gap-1 text-sm font-semibold text-grey-700 mb-1.5">
                        Approver Email Addresses
                        <div className="relative group cursor-pointer">
                          <Info className="w-4 h-4 text-grey-400" />
                          <div className="absolute left-1/2 z-10 hidden group-hover:block group-focus:block min-w-[260px] -translate-x-1/2 mt-2 px-3 py-2 rounded-lg bg-[#F2F4F7] text-xs text-grey-700 border border-grey-200 shadow-xl">
                            Once all failed checks are either resolved or
                            &apos;In review&apos;, these approvers will be
                            notified.
                          </div>
                        </div>
                      </label>
                      <div className="w-full">
                        {approverEmails.map((email, idx) => (
                          <div key={idx} className="flex mb-2">
                            <Input
                              type="email"
                              placeholder="example@email.com"
                              value={email}
                              inputClassName="w-full"
                              onChange={(e) => {
                                const val = e.target.value;
                                setApproverEmails((emails) =>
                                  emails.map((em, i) => (i === idx ? val : em))
                                );
                                // Validate and clear error immediately if valid
                                // Check for duplicate emails
                                const updatedEmails = approverEmails.map(
                                  (em, i) => (i === idx ? val : em)
                                );
                                const isDuplicate =
                                  val &&
                                  updatedEmails.some(
                                    (email, i) =>
                                      i !== idx &&
                                      email.toLowerCase() === val.toLowerCase()
                                  );

                                setApproverEmailErrors((errors) =>
                                  errors.map((err, i) =>
                                    i === idx
                                      ? val &&
                                        emailRegex.test(val) &&
                                        !isDuplicate
                                        ? ''
                                        : err
                                      : err
                                  )
                                );
                                // Clear the form-level error if at least one valid email exists
                                const hasValidEmail = updatedEmails.some(
                                  (email) =>
                                    email.trim() !== '' &&
                                    emailRegex.test(email) &&
                                    !updatedEmails.some(
                                      (e, i) =>
                                        i !== updatedEmails.indexOf(email) &&
                                        e.toLowerCase() === email.toLowerCase()
                                    )
                                );
                                if (hasValidEmail && errors.approverEmails) {
                                  clearErrors('approverEmails');
                                }
                              }}
                              onBlur={(e) => {
                                const val = e.target.value;
                                // Check for duplicate emails
                                const isDuplicate =
                                  val &&
                                  approverEmails.some(
                                    (email, i) =>
                                      i !== idx &&
                                      email.toLowerCase() === val.toLowerCase()
                                  );

                                setApproverEmailErrors((errors) =>
                                  errors.map((err, i) =>
                                    i === idx
                                      ? val && !emailRegex.test(val)
                                        ? 'Enter a valid email address.'
                                        : isDuplicate
                                          ? 'This email address has already been added.'
                                          : ''
                                      : err
                                  )
                                );
                              }}
                              autoComplete="off"
                            />
                            <button
                              type="button"
                              className="ml-2 text-grey-400 hover:text-grey-700 cursor-pointer"
                              aria-label="Remove email"
                              onClick={() => {
                                setApproverEmails((emails) =>
                                  emails.filter((_, i) => i !== idx)
                                );
                                setApproverEmailErrors((errors) =>
                                  errors.filter((_, i) => i !== idx)
                                );
                              }}>
                              <CloseIcon />
                            </button>
                          </div>
                        ))}
                      </div>

                      {approverEmailErrors.some(Boolean) && (
                        <div className="text-error-light text-2sm mt-1">
                          {approverEmailErrors.map(
                            (err, idx) => err && <div key={idx}>{err}</div>
                          )}
                        </div>
                      )}
                      {errors.approverEmails && (
                        <div className="text-error-light text-2sm mt-1">
                          {errors.approverEmails?.message?.toString()}
                        </div>
                      )}
                      {approverEmails.length < 5 && (
                        <button
                          type="button"
                          className="mt-2 rounded border border-grey-300 bg-white px-4 py-2 text-[13.5px] font-normal text-[#1257A0] hover:bg-grey-50"
                          onClick={() => {
                            setApproverEmails((emails) => [...emails, '']);
                            setApproverEmailErrors((errors) => [...errors, '']);
                          }}>
                          + Add another email address
                        </button>
                      )}
                      {approverEmails.length >= 5 && (
                        <p className="field-hint mt-2">
                          Maximum 5 approver emails allowed
                        </p>
                      )}
                    </div>
                    )}
                  </WorkflowChoice>

                  <div>
                    <h3 className="text-sm font-semibold text-grey-900">
                      Blocking checks
                    </h3>
                    <p className="mt-1 text-sm text-grey-500">
                      Turn on the checks that should block. Anything left off is
                      still run and recorded on the inspection, but won&apos;t
                      block it.
                    </p>
                    <div className="mt-4">
                      <BlockingChecksSelector
                        assetType={assetType}
                        selectedCheckIds={selectedBlockingCheckIds}
                        onChange={setSelectedBlockingCheckIds}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <TemplateTabNav activeTab={activeTab} onChange={goToTab} />
        </div>
      </div>
      <div
        className={clsx(
          'fixed inset-x-0 z-30 border-y border-grey-200 bg-white',
          LAYOUT_FOOTER_OFFSET
        )}>
        <div className="mx-auto flex w-full max-w-[768px] flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-[#475467]">
            Step {activeTabIndex + 1} of {TEMPLATE_TABS.length} &middot;{' '}
            {selectedDocumentCount}{' '}
            {selectedDocumentCount === 1 ? 'document' : 'documents'} required
            {' · '}
            {formatBlockingCheckFooter(blockingCheckCount)}
          </p>
          <Button
            className={clsx(
              'w-full min-w-0 !rounded px-6 !text-sm font-semibold capitalize md:w-auto md:min-w-[240px] md:px-10 md:!text-sm',
              saveState === 'saved' ? 'bg-success-600' : 'bg-primary'
            )}
            disabled={saveState !== 'idle'}
            onClick={handleSaveAndClose}>
            {SAVE_BUTTON_LABEL[saveState]}
          </Button>
        </div>
      </div>
      {toast && <SaveToast message={toast.message} variant={toast.variant} />}
      {saveState === 'closing' && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-50 bg-grey-900 bg-opacity-40"
        />
      )}
      <ConfirmInspectionModal
        open={isOpenModal}
        type={modalType}
        onSubmit={() => handleClose()}
        onClose={() => onCloseModalClick()}
      />
    </OnboardingLayout>
  );
};
export default CreateTemplate;
